import React, { useState } from "react";
import Icon from "@/components/ui/Icon";
import HorizentalMenu from "./Tools/HorizentalMenu";
import useWidth from "@/hooks/useWidth";
import useNavbarType from "@/hooks/useNavbarType";
import useMenulayout from "@/hooks/useMenulayout";
import useSkin from "@/hooks/useSkin";
import Logo from "./Tools/Logo";
import Profile from "./Tools/Profile";
import Notification from "./Tools/Notification";
import HeaderSearch from "./Tools/HeaderSearch";
import { Tooltip, message } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import apiClient from "@/server/server";
import { useDispatch, useSelector } from "react-redux";
import { handleMobileMenu } from "@/store/layout";
import BrandWordmark from "@/components/ui/BrandWordmark";

const Header = () => {
  const { width, breakpoints } = useWidth();
  const [syncing, setSyncing] = useState(false);
  const dispatch = useDispatch();
  const sidebarCollapsed = useSelector((s) =>
    Boolean(s.layout?.isCollapsed),
  );
  const mdSidebarLeft = sidebarCollapsed ? "md:left-16" : "md:left-52";

  const handleSyncProfiles = async () => {
    try {
      setSyncing(true);
      await apiClient.post("/meilisearch/sync/profiles");
      message.success("Profiles re-indexed successfully!");
    } catch (error) {
      console.error("Sync error:", error);
      message.error("Failed to re-index profiles");
    } finally {
      setSyncing(false);
    }
  };

  const [navbarType] = useNavbarType();
  const navbarTypeClass = () => {
    switch (navbarType) {
      case "floating":
        return "floating has-sticky-header font-jakarta";
      case "sticky":
        return `fixed top-0 left-0 right-0 z-[999] font-jakarta transition-[left] duration-200 ease-out ${mdSidebarLeft}`;
      case "static":
        return "static font-jakarta";
      case "hidden":
        return "hidden";
      default:
        return `fixed top-0 left-0 right-0 z-[999] font-jakarta transition-[left] duration-200 ease-out ${mdSidebarLeft}`;
    }
  };

  const [menuType] = useMenulayout();
  const [skin] = useSkin();

  const borderSwicthClass = () => {
    if (skin === "bordered" && navbarType !== "floating") {
      return "border-b border-stone-200 dark:border-slate-700";
    }
    if (skin === "bordered" && navbarType === "floating") {
      return "border border-stone-200 dark:border-slate-700";
    }
    return "dark:border-b dark:border-slate-700 dark:border-opacity-60";
  };

  return (
    <header className={navbarTypeClass()}>
      <div
        className={`app-header flex h-16 items-center border-b border-stone-200/80 bg-white/90 px-4 shadow-sm backdrop-blur-md dark:border-slate-800 dark:bg-slate-950/90 md:px-8
        ${borderSwicthClass()}
        ${menuType === "horizontal" && width > breakpoints.xl ? "md:h-[4.25rem]" : ""}
        `}
      >
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-3 md:gap-6">
            <button
              type="button"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-stone-50 text-slate-700 transition hover:bg-stone-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 md:hidden"
              aria-label="Open navigation menu"
              onClick={() => dispatch(handleMobileMenu(true))}
            >
              <Icon icon="heroicons-outline:menu-alt-2" className="text-xl" />
            </button>

            {menuType === "horizontal" && width > breakpoints.xl ? (
              <div className="flex min-w-0 items-center gap-6">
                <Logo />
                <div className="hidden min-w-0 xl:block">
                  <HorizentalMenu />
                </div>
              </div>
            ) : (
              <div className="min-w-0 md:pl-0">
                <BrandWordmark variant="header" />
              </div>
            )}
          </div>

          {width >= breakpoints.md && (
            <div className="hidden flex-1 justify-center px-4 md:flex">
              <HeaderSearch />
            </div>
          )}

          <div className="nav-tools flex shrink-0 items-center space-x-2 rtl:space-x-reverse md:space-x-4">
            {width >= breakpoints.md && (
              <Tooltip title="Re-index Profiles (Meilisearch)">
                <button
                  type="button"
                  onClick={handleSyncProfiles}
                  disabled={syncing}
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-transparent text-slate-500 transition hover:border-stone-200 hover:bg-stone-50 hover:text-teal-700 disabled:opacity-50 dark:text-slate-400 dark:hover:border-slate-700 dark:hover:bg-slate-900 dark:hover:text-teal-400"
                >
                  <SyncOutlined
                    spin={syncing}
                    className={`text-lg ${syncing ? "text-teal-600" : ""}`}
                  />
                </button>
              </Tooltip>
            )}
            {width >= breakpoints.md && <Notification />}
            {width >= breakpoints.md && <Profile />}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
